import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import Dropdown from "../../../../components/common/Dropdown";
import DeleteModal from "../../../../components/modals/DeleteModal";
import Icon from "../../../../components/common/Icon";
import { getScanConfig, getScannableObjects, ScannableObjectResponse } from "../../../../api/progression";
import {
    getWaypointsForTrack,
    deleteWaypoint as deleteWaypointAPI,
    saveTemporaryWaypoint,
} from "../../../../api/waypoint";
import { applyUpdatesToTrack } from "../../../../api/track";
import { useSortableSensors } from "../../../../hooks/useSortableSensors";
import { getStableInternalId } from "../../../../utils/getStableInternalId";
import i18n from "../../../../i18n";
import styles from "./ManageStories.module.css";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface ManageStoriesProps {
    navigateToNext: () => void;
}

interface WaypointPicture {
    id?: string;
    url: string;
}

interface LocalWaypoint {
    id: string;
    internalId?: string;
    position: number;
    status?: boolean;
    scannableObjectId?: string;
    triggerMode?: string;
    pictures?: WaypointPicture[] | string[];
    waypointId?: string | null;
    trackId?: string;
    waypointLocalized?: Array<{
        name?: string;
        language: string;
    }>;
    // For temporary waypoints
    TemporaryWaypointLocalized?: Array<{
        title: string;
        language: string;
    }>;
    isTemporary?: boolean; // Flag to identify draft/temporary waypoints
}

interface StoryCardContentProps {
    wpt: LocalWaypoint;
    name: string;
    firstPicture: string | null;
    objectName?: string | null;
    onEdit: () => void;
    onDeleteClick: () => void;
    t: any;
}

const StoryCardContent: React.FC<StoryCardContentProps> = ({
    wpt,
    name,
    firstPicture,
    objectName,
    onEdit,
    onDeleteClick,
    t,
}) => (
    <>
        {wpt.isTemporary && (
            <Tooltip title={t("draftWillBecomeLiveWhenPublished")} arrow>
                <div className={styles.draftBadge}>{t("draft")}</div>
            </Tooltip>
        )}

        <div className={styles.storyCardHeader}>
            <div className={styles.storyImage}>
                {firstPicture ? (
                    <img src={firstPicture} alt={name} />
                ) : (
                    <Icon name="ic-image" className={styles.storyImagePlaceholder} size={22} />
                )}
            </div>
            <div className={styles.storyInfo}>
                <div className={styles.storyNameRow}>
                    <h4 className={styles.storyName}>{name}</h4>
                </div>
                <div className={styles.storyMeta}>
                    <span className={styles.metaItem}>
                        {t("position")}: {wpt.position}
                    </span>
                    {objectName && (
                        <span className={`${styles.metaItem} ${styles.metaObject}`}>
                            {t("object")}: {objectName}
                        </span>
                    )}
                </div>
            </div>
        </div>

        <div className={styles.storyCardActions}>
            <div className={styles.actionButtons}>
                <Tooltip title={t("edit")} arrow>
                    <button
                        type="button"
                        className={styles.editBtn}
                        aria-label={t("edit")}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit();
                        }}
                    >
                        <Icon name="ic-edit" size={20} />
                    </button>
                </Tooltip>
                <Tooltip title={t("delete")} arrow>
                    <button
                        type="button"
                        className={styles.deleteBtn}
                        aria-label={t("delete")}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteClick();
                        }}
                    >
                        <Icon name="ic-trash" size={20} />
                    </button>
                </Tooltip>
            </div>
        </div>
    </>
);

interface SortableStoryCardProps extends StoryCardContentProps {}

const SortableStoryCard: React.FC<SortableStoryCardProps> = (props) => {
    const sortableId = props.wpt.id || props.wpt.internalId || "";
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        // Hide the original while the DragOverlay renders the floating preview
        opacity: isDragging ? 0 : 1,
        cursor: "grab",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={styles.storyCard}
            onClick={props.onEdit}
            {...attributes}
            {...listeners}
        >
            <StoryCardContent {...props} />
        </div>
    );
};

const ManageStories: React.FC<ManageStoriesProps> = ({ navigateToNext }) => {
    const { trackData, selectedStoryObjectId, setSelectedStoryObjectId } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [objects, setObjects] = useState<ScannableObjectResponse[]>([]);
    const [waypoints, setWaypoints] = useState<LocalWaypoint[]>([]);
    // Use context state for filter persistence across route changes
    const selectedObjectId = selectedStoryObjectId;
    const setSelectedObjectId = setSelectedStoryObjectId;
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState<LocalWaypoint | null>(null);
    const [publishing, setPublishing] = useState<boolean>(false);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    const sensors = useSortableSensors();

    const linkId = trackData?.trackId || trackData?.id;

    const loadData = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);

            // Load scannable objects
            const configResponse = await getScanConfig({ token, trackId: trackData.trackId });
            if (configResponse?.scanConfig) {
                const objectsResponse = await getScannableObjects({
                    token,
                    scanConfigId: configResponse.scanConfig.id,
                });
                if (objectsResponse?.scannableObjects) {
                    setObjects(objectsResponse.scannableObjects.sort((a, b) => a.position - b.position));
                }
            }

            // Load waypoints (both definitive and temporary)
            const waypointsResponse = await getWaypointsForTrack({
                token,
                trackId: trackData.trackId,
            });

            // Index definitive waypoints by id so drafts linked to a published
            // waypoint can fall back to its localized content / pictures for
            // display when the draft itself has none yet (e.g. drafts created
            // by a reorder that only persists `position`).
            const definitiveById = new Map<string, any>(
                (waypointsResponse?.waypoints || []).map((w: any) => [w.id, w]),
            );

            // Merge definitive waypoints and temporary waypoints
            const temporaryWaypoints: LocalWaypoint[] = (waypointsResponse?.temporaryWaypoints || []).map(
                (wpt: any) => {
                    const linked = wpt.waypointId ? definitiveById.get(wpt.waypointId) : null;
                    const hasLocalizedDraft =
                        Array.isArray(wpt.TemporaryWaypointLocalized) && wpt.TemporaryWaypointLocalized.length > 0;
                    const hasPicturesDraft = Array.isArray(wpt.pictures) && wpt.pictures.length > 0;
                    // Display-only enrichment: this never leaves the frontend, so
                    // no risk of overwriting the definitive waypoint's content.
                    return {
                        ...wpt,
                        id: wpt.id || wpt.internalId, // Use internalId as id for temporary waypoints
                        isTemporary: true,
                        waypointLocalized:
                            !hasLocalizedDraft && linked?.waypointLocalized
                                ? linked.waypointLocalized
                                : wpt.waypointLocalized,
                        pictures: !hasPicturesDraft && linked?.pictures ? linked.pictures : wpt.pictures,
                    };
                },
            );

            // Get the IDs of definitive waypoints that have a corresponding temporary waypoint
            // (these are waypoints being edited - we only want to show the draft version)
            const definitiveIdsWithDrafts = new Set(
                temporaryWaypoints.filter((wpt: any) => wpt.waypointId).map((wpt: any) => wpt.waypointId),
            );

            // Filter out definitive waypoints that have a pending draft (to avoid duplicates)
            const definitiveWaypoints: LocalWaypoint[] = (waypointsResponse?.waypoints || [])
                .filter((wpt: any) => !definitiveIdsWithDrafts.has(wpt.id))
                .map((wpt: any) => ({
                    ...wpt,
                    isTemporary: false,
                }));

            // Combine and sort by position
            const allWaypoints = [...definitiveWaypoints, ...temporaryWaypoints].sort(
                (a, b) => (a.position || 0) - (b.position || 0),
            );

            setWaypoints(allWaypoints);
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // fetch-on-mount when track changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, token]);

    const getObjectLocalizedName = (obj: ScannableObjectResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = obj.scannableObjectLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || obj.scannableObjectLocalized?.[0]?.name || t("unnamed");
    };

    const getWaypointLocalizedName = (wpt: LocalWaypoint) => {
        const langCode = i18n.language?.toUpperCase();

        // Check definitive waypoint localized content
        if (wpt.waypointLocalized && wpt.waypointLocalized.length > 0) {
            const localized = wpt.waypointLocalized.find((loc) => loc.language?.toUpperCase() === langCode);
            if (localized?.name) return localized.name;
            // Fallback to first available
            if (wpt.waypointLocalized[0]?.name) return wpt.waypointLocalized[0].name;
        }

        // Check temporary waypoint localized content
        if (wpt.TemporaryWaypointLocalized && wpt.TemporaryWaypointLocalized.length > 0) {
            const localized = wpt.TemporaryWaypointLocalized.find((loc) => loc.language?.toUpperCase() === langCode);
            if (localized?.title) return localized.title;
            // Fallback to first available
            if (wpt.TemporaryWaypointLocalized[0]?.title) return wpt.TemporaryWaypointLocalized[0].title;
        }

        return t("story") + " " + wpt.position;
    };

    const getObjectNameById = (objectId: string) => {
        const obj = objects.find((o) => o.id === objectId);
        return obj ? getObjectLocalizedName(obj) : t("unassigned");
    };

    const objectOptions = useMemo(() => {
        return [
            { value: "all", label: t("allObjects") },
            ...objects
                .filter((obj) => obj.status)
                .map((obj) => ({
                    value: obj.id,
                    label: getObjectLocalizedName(obj),
                })),
        ];
        // getObjectLocalizedName depends on i18n.language (stable within render)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objects, t]);

    const filteredWaypoints = useMemo(() => {
        // Only show IMAGE_SCAN or BOTH or INVISIBLE trigger mode waypoints
        const imageScanWaypoints = waypoints.filter(
            (wpt) => wpt.triggerMode === "IMAGE_SCAN" || wpt.triggerMode === "BOTH" || wpt.triggerMode === "INVISIBLE",
        );

        if (!selectedObjectId || selectedObjectId === "all") {
            return imageScanWaypoints.sort((a, b) => a.position - b.position);
        }
        return imageScanWaypoints
            .filter((wpt) => wpt.scannableObjectId === selectedObjectId)
            .sort((a, b) => a.position - b.position);
    }, [waypoints, selectedObjectId]);

    // Group stories by scannable object (for "all" view)
    // Sorted alphabetically by object name, then by position within each group
    const groupedWaypoints = useMemo(() => {
        if (selectedObjectId && selectedObjectId !== "all") {
            // When a specific object is selected, don't group - just return as single group
            return null;
        }

        // Only show IMAGE_SCAN or BOTH or INVISIBLE trigger mode waypoints
        const imageScanWaypoints = waypoints.filter(
            (wpt) => wpt.triggerMode === "IMAGE_SCAN" || wpt.triggerMode === "BOTH" || wpt.triggerMode === "INVISIBLE",
        );

        // Group waypoints by scannableObjectId
        const groups: Record<string, { objectName: string; objectId: string; waypoints: LocalWaypoint[] }> = {};
        const unassignedGroup: LocalWaypoint[] = [];

        imageScanWaypoints.forEach((wpt) => {
            if (wpt.scannableObjectId) {
                if (!groups[wpt.scannableObjectId]) {
                    const obj = objects.find((o) => o.id === wpt.scannableObjectId);
                    groups[wpt.scannableObjectId] = {
                        objectId: wpt.scannableObjectId,
                        objectName: obj ? getObjectLocalizedName(obj) : t("unnamed"),
                        waypoints: [],
                    };
                }
                groups[wpt.scannableObjectId].waypoints.push(wpt);
            } else {
                unassignedGroup.push(wpt);
            }
        });

        // Sort waypoints within each group by position
        Object.values(groups).forEach((group) => {
            group.waypoints.sort((a, b) => a.position - b.position);
        });
        unassignedGroup.sort((a, b) => a.position - b.position);

        // Convert to array and sort by scannable object position (using objects array order)
        const sortedGroups = Object.values(groups).sort((a, b) => {
            const objA = objects.find((o) => o.id === a.objectId);
            const objB = objects.find((o) => o.id === b.objectId);
            const posA = objA?.position ?? Number.MAX_SAFE_INTEGER;
            const posB = objB?.position ?? Number.MAX_SAFE_INTEGER;
            return posA - posB;
        });

        // Add unassigned group at the end if it has waypoints
        if (unassignedGroup.length > 0) {
            sortedGroups.push({
                objectId: "unassigned",
                objectName: t("unassigned"),
                waypoints: unassignedGroup,
            });
        }

        return sortedGroups;
        // getObjectLocalizedName depends on i18n.language (stable within render)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waypoints, selectedObjectId, objects, t]);

    // Build a lookup from waypoint id -> group of waypoints, so that onDragEnd
    // can determine whether active and over belong to the same object group and
    // reject cross-group drags.
    const waypointGroups = useMemo((): Record<string, LocalWaypoint[]> => {
        const getKey = (w: LocalWaypoint) => w.id || w.internalId || "";
        const groups: Record<string, LocalWaypoint[]> = {};
        if (groupedWaypoints) {
            groupedWaypoints.forEach((group) => {
                group.waypoints.forEach((w) => {
                    groups[getKey(w)] = group.waypoints;
                });
            });
        } else {
            filteredWaypoints.forEach((w) => {
                groups[getKey(w)] = filteredWaypoints;
            });
        }
        return groups;
    }, [groupedWaypoints, filteredWaypoints]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveDragId(String(event.active.id));
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveDragId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        if (!trackData.trackId) return;

        const activeId = String(active.id);
        const overId = String(over.id);
        const activeGroup = waypointGroups[activeId];
        const overGroup = waypointGroups[overId];
        // Enforce within-group reordering only
        if (!activeGroup || !overGroup || activeGroup !== overGroup) return;

        const groupWaypoints = activeGroup;
        const getKey = (w: LocalWaypoint) => w.id || w.internalId || "";

        const oldIndex = groupWaypoints.findIndex((w) => getKey(w) === activeId);
        const newIndex = groupWaypoints.findIndex((w) => getKey(w) === overId);
        if (oldIndex === -1 || newIndex === -1) return;

        // Preserve the existing position values from this group so neighbouring
        // groups (which share the same global position space) remain untouched.
        // The same position values are reassigned to the items in the new order.
        //
        // Edge case: if two items in the group share the same position (can
        // happen with legacy data or concurrent creations), the pool contains
        // duplicates, which makes the reassignment a no-op and the drag looks
        // "blocked". Expand duplicates into a unique sequence starting at the
        // group's minimum position so a reorder always has a measurable effect.
        const sortedPool = [...groupWaypoints.map((w) => w.position)].sort((a, b) => a - b);
        const hasDuplicatePositions = new Set(sortedPool).size !== sortedPool.length;
        const positionsPool = hasDuplicatePositions ? sortedPool.map((_, i) => sortedPool[0] + i) : sortedPool;
        const reordered = arrayMove(groupWaypoints, oldIndex, newIndex);
        const reassigned = reordered.map((w, i) => ({ ...w, position: positionsPool[i] }));

        const changed = reassigned.filter((w) => {
            const original = groupWaypoints.find((g) => getKey(g) === getKey(w));
            return original && original.position !== w.position;
        });

        if (changed.length === 0) return;

        const reassignedByKey = new Map(reassigned.map((w) => [getKey(w), w] as const));
        const changedKeys = new Set(changed.map(getKey));

        setWaypoints((prev) =>
            prev.map((w) => {
                const key = w.id || w.internalId || "";
                const updatedItem = reassignedByKey.get(key);
                if (!updatedItem) return w;
                const wasChanged = changedKeys.has(key);
                return {
                    ...w,
                    position: updatedItem.position,
                    ...(wasChanged ? { isTemporary: true } : {}),
                };
            }),
        );

        try {
            await Promise.all(
                changed.map((w) => {
                    const internalId = getStableInternalId(w);
                    const waypointIdForSave = w.isTemporary ? w.waypointId || null : w.id;
                    // Intentionally do NOT send localizedContent / pictures
                    // here. The backend's saveLocalizedContent deletes any
                    // language not present in the payload, so sending a stale
                    // snapshot from a reorder could silently drop content
                    // that a concurrent editor added. The missing title/image
                    // on reload is addressed purely on the frontend by
                    // merging the linked definitive waypoint's content into
                    // draft rows for display in loadData.
                    return saveTemporaryWaypoint({
                        token,
                        internalId,
                        trackId: trackData.trackId!,
                        waypointId: waypointIdForSave,
                        position: w.position,
                        scannableObjectId: w.scannableObjectId,
                        triggerMode: w.triggerMode,
                    } as any);
                }),
            );
        } catch (error) {
            console.error("Error reordering stories:", error);
            await loadData();
        }
    };

    const handleCreate = () => {
        // Navigate to create story with selected object pre-selected
        const objParam = selectedObjectId && selectedObjectId !== "all" ? selectedObjectId : "";
        navigate(`/create/tour/${linkId}/stories/create${objParam ? `/${objParam}` : ""}`);
    };

    const handleEdit = (storyId: string) => {
        navigate(`/create/tour/${linkId}/stories/modify/${storyId}`);
    };

    const handleDelete = async () => {
        if (!storyToDelete) return;

        // Optimistic update
        const deletedStory = storyToDelete;
        setWaypoints((prev) => prev.filter((wpt) => wpt.id !== deletedStory.id));
        setDeleteModalOpen(false);
        setStoryToDelete(null);

        try {
            await deleteWaypointAPI({ token, waypointId: deletedStory.id, trackId: trackData.trackId || "" });
        } catch (error) {
            console.error("Error deleting story:", error);
            // Revert on error
            setWaypoints((prev) => [...prev, deletedStory].sort((a, b) => a.position - b.position));
        }
    };

    const handlePublishAll = async () => {
        if (!trackData.trackId) return;

        setPublishing(true);
        try {
            await applyUpdatesToTrack({ token, trackId: trackData.trackId });
            // Reload data to refresh the list
            await loadData();
        } catch (error) {
            console.error("Error publishing stories:", error);
        } finally {
            setPublishing(false);
        }
    };

    const hasTemporaryStories = waypoints.some((wpt) => wpt.isTemporary);

    const getFirstPicture = (wpt: LocalWaypoint) => {
        if (wpt.pictures && wpt.pictures.length > 0) {
            return typeof wpt.pictures[0] === "string" ? wpt.pictures[0] : (wpt.pictures[0] as any)?.url;
        }
        return null;
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/objects`}>{t("objectsAndStories")}</Link>
                <span>›</span>
                <Link to={`/create/tour/${linkId}/objects`}>{t("objects")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>
                    {t("stories")}:{" "}
                    {selectedObjectId && selectedObjectId !== "all" ? getObjectNameById(selectedObjectId) : t("all")}
                </span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createAndManageStories")}</h2>
                        <p className={pageHeader.subtitle}>{t("createStoriesForSelectedObject")}</p>
                    </div>
                    <div className={styles.headerActions}>
                        <AddButton onClick={handleCreate} tooltip={t("addNewStory")} disabled={objects.length === 0} />
                    </div>
                </div>
            </div>

            {/* Object filter */}
            <div className={styles.filterSection}>
                <div className={styles.filterLabel}>{t("selectedObject")}</div>
                <div className={styles.filterDescription}>{t("storiesWillBeAssociatedToObject")}</div>
                <Dropdown
                    label=""
                    value={selectedObjectId}
                    options={objectOptions}
                    onChange={setSelectedObjectId}
                    placeholder={t("selectObject")}
                    loading={false}
                    error={false}
                />
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : filteredWaypoints.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noStoriesYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noStoriesDescription")}</p>
                    <AddButton onClick={handleCreate} tooltip={t("createFirstStory")} disabled={objects.length === 0} />
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setActiveDragId(null)}
                >
                    {groupedWaypoints ? (
                        /* Grouped view - when "all" is selected, group by object alphabetically */
                        <div className={styles.groupedContainer}>
                            {groupedWaypoints.map((group) => (
                                <div key={group.objectId} className={styles.objectGroup}>
                                    <div className={styles.groupHeader}>
                                        <h3 className={styles.groupTitle}>{group.objectName}</h3>
                                        <span className={styles.groupCount}>
                                            {group.waypoints.length}{" "}
                                            {group.waypoints.length === 1 ? t("story") : t("stories")}
                                        </span>
                                    </div>
                                    <SortableContext
                                        items={group.waypoints.map((w) => w.id || w.internalId || "")}
                                        strategy={rectSortingStrategy}
                                    >
                                        <div className={styles.storiesGrid}>
                                            {group.waypoints.map((wpt) => (
                                                <SortableStoryCard
                                                    key={wpt.id || wpt.internalId}
                                                    wpt={wpt}
                                                    name={getWaypointLocalizedName(wpt)}
                                                    firstPicture={getFirstPicture(wpt)}
                                                    onEdit={() => handleEdit(wpt.id)}
                                                    onDeleteClick={() => {
                                                        setStoryToDelete(wpt);
                                                        setDeleteModalOpen(true);
                                                    }}
                                                    t={t}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Non-grouped view - when a specific object is selected */
                        <SortableContext
                            items={filteredWaypoints.map((w) => w.id || w.internalId || "")}
                            strategy={rectSortingStrategy}
                        >
                            <div className={styles.storiesGrid}>
                                {filteredWaypoints.map((wpt) => (
                                    <SortableStoryCard
                                        key={wpt.id || wpt.internalId}
                                        wpt={wpt}
                                        name={getWaypointLocalizedName(wpt)}
                                        firstPicture={getFirstPicture(wpt)}
                                        objectName={
                                            wpt.scannableObjectId ? getObjectNameById(wpt.scannableObjectId) : null
                                        }
                                        onEdit={() => handleEdit(wpt.id)}
                                        onDeleteClick={() => {
                                            setStoryToDelete(wpt);
                                            setDeleteModalOpen(true);
                                        }}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    )}

                    <DragOverlay dropAnimation={null}>
                        {activeDragId
                            ? (() => {
                                  const wpt = waypoints.find((w) => (w.id || w.internalId) === activeDragId);
                                  if (!wpt) return null;
                                  return (
                                      <div className={styles.storyCard} style={{ cursor: "grabbing" }}>
                                          <StoryCardContent
                                              wpt={wpt}
                                              name={getWaypointLocalizedName(wpt)}
                                              firstPicture={getFirstPicture(wpt)}
                                              objectName={
                                                  !groupedWaypoints && wpt.scannableObjectId
                                                      ? getObjectNameById(wpt.scannableObjectId)
                                                      : null
                                              }
                                              onEdit={() => {}}
                                              onDeleteClick={() => {}}
                                              t={t}
                                          />
                                      </div>
                                  );
                              })()
                            : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Action buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                {hasTemporaryStories && (
                    <Button
                        label={publishing ? t("publishing") : t("publishAllStories")}
                        onClick={handlePublishAll}
                        disabled={publishing || !trackData.trackId}
                        variant="outline"
                        shape="pill"
                        className={stepStyles.footerBtn}
                    />
                )}
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    disabled={filteredWaypoints.length === 0}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {storyToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setStoryToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteStoryConfirmation")}
                    title={t("deleteStory")}
                />
            )}
        </div>
    );
};

export default ManageStories;
